/**
 * English (en) base catalog — the DEFAULT locale and the fallback for every
 * other locale (#42, 2026-06-12).
 *
 * This file is eagerly imported by the i18n provider, so it ships in the main
 * app chunk. It is intentionally small — only the slice that is actually wired
 * end-to-end is translated (the auth sign-in screen, the cross-trust nav
 * labels, and a handful of shared/common strings). The WHOLE app is NOT
 * translated; see lib/i18n/README note in `index.ts` for the extend pattern.
 *
 * `en` is also the SHAPE of the catalog: the `Catalog` type is derived from it
 * (`typeof EN`), so every other locale catalog is type-checked to provide the
 * same keys, and `t(key)` keys are autocompleted + checked at the call site.
 *
 * Keys are namespaced with a dotted convention: `auth.*`, `nav.*`, `common.*`.
 * The catalog is a FLAT record keyed by the dotted string (not a nested
 * object) — this keeps the `t()` lookup a single property read and the
 * `TranslationKey` union trivial to derive.
 */

export const EN = {
  // ── common.* — shared UI primitives ──────────────────────────────────────
  'common.language': 'Language',
  'common.english': 'English',
  'common.afrikaans': 'Afrikaans',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.loading': 'Loading…',

  // ── nav.* — cross-trust sidebar nav labels ───────────────────────────────
  'nav.overview': 'Dashboard',
  'nav.myTrusts': 'My trusts',
  'nav.inbox': 'Inbox',
  'nav.security': 'Security',
  'nav.firmAccess': 'Firm access',
  'nav.privacy': 'Privacy',
  'nav.profile': 'Profile',

  // ── auth.* — the sign-in screen, end-to-end ──────────────────────────────
  'auth.eyebrow': 'Sign in',
  'auth.signInToElreign': 'Sign in to Elreign.',
  'auth.sub': 'Enter your email and password. New accounts are created from the signup page.',
  'auth.emailLabel': 'Email address',
  'auth.passwordLabel': 'Password',
  'auth.forgotPassword': 'Forgot your password?',
  'auth.signIn': 'Sign in',
  'auth.signingIn': 'Signing in…',
  'auth.noAccount': "Don't have an account?",
  'auth.createOne': 'Create one',
  // Inline validation / error strings surfaced on the sign-in form.
  'auth.errEmailRequired': 'Enter your email address to continue.',
  'auth.errEmailFormat': 'Email format is name@example.co.za.',
  'auth.errPasswordRequired': 'Enter your password to continue.',
} as const;

/** The catalog shape — every locale must satisfy this exact key set. */
export type Catalog = Record<keyof typeof EN, string>;

/** The union of every translation key (e.g. `'auth.signIn' | 'nav.inbox' | …`). */
export type TranslationKey = keyof typeof EN;
